import { compareStrings, highlightSearch } from '@/filters/helpers';
import clone from 'lodash/clone';

export default {
  users: (state) => {
    return state.users;
  },
  usersLoaded: (state) => {
    return state.usersLoaded;
  },
  activeUsers: (state) => {
    return state.users.filter(({ status }) => {
      return status == '1' || typeof status === 'undefined';
    });
  },
  archivedUsers: (state) => {
    return state.users.filter((user) => user.status == '0');
  },
  filteredUsers: (state, getters) => {
    return (active = true) => {
      const users = active ? getters.activeUsers : getters.archivedUsers;
      return filterUsers(users, state.usersSearch);
    };
  },
  usersOptions: (state, getters) => {
    return formatUsers(getters.activeUsers);
  },
  usersView: (state) => {
    return state.usersView;
  },
  usersSearch: (state) => {
    return state.usersSearch;
  },
  usersByIdMap(state) {
    const map = Object.create(null);
    for (const user of state.users) {
      map[user.id] = user;
    }
    return map;
  },
  singleUser(state, getters) {
    return (userId) => getters.usersByIdMap[userId] ?? null;
  },
  userLoading(state) {
    return state.userLoading;
  },
  currentUser: (state) => {
    return state.currentUser;
  },
  userDefaults: (state) => {
    return state.currentUser?.defaults || {};
  },
  userTeam: (state) => {
    return state.currentUser?.selected_team;
  },
  isUserLoggedIn: (state) => {
    return !!state.currentUser;
  },
  currentUserLoaded: (state) => {
    return state.currentUserLoaded;
  },
  isAdmin: (state) => {
    return (state.currentUser || {}).role === 'admin';
  },
  isContactInviteEnabled: (state) => {
    return state.currentUser?.org_plan === 'portal_search';
  },
  // check is feature enabled for organization
  isFeatureEnabled: (state) => {
    return (feature) => {
      const features = state?.currentUser?.features || {};
      return features[feature] || false;
    };
  },
  // check if user has permission
  hasPermission: (state) => {
    return (feature) => {
      if (!state.currentUser) return false;
      const { feature_flags } = state.currentUser;
      return (feature_flags || []).includes(feature);
    };
  },
  // check if user has any of the given permissions (OR condition)
  hasAnyPermission: (state) => {
    return (permissions) => {
      if (!state.currentUser) return false;
      if (!permissions?.length) return true;
      const { feature_flags } = state.currentUser;
      return permissions.some((p) => (feature_flags || []).includes(p));
    };
  },
  isVectorRecruiting: (state, getters) => {
    return getters.isFeatureEnabled('vmc_recruiting');
  },
  hasAccessToPage: (state, getters) => {
    return (pageMeta) => {
      const { feature, permission, permissions } = pageMeta;
      const primaryNavFeatures = [
        'groups',
        'conversations',
        'campaigns',
        'lessons',
        'automations',
        'reports',
      ];

      // Single permission check
      const hasSinglePermission =
        !permission || getters.hasPermission(permission);

      // Multiple permissions check (OR condition - user needs ANY of the permissions)
      const hasAnyPermission =
        !permissions?.length || getters.hasAnyPermission(permissions);

      let featureEnabled = !feature || getters.isFeatureEnabled(feature);
      if (
        !featureEnabled &&
        primaryNavFeatures.includes(feature) &&
        getters.isFeatureEnabled('primary_navigation')
      ) {
        featureEnabled = true;
      }

      return featureEnabled && hasSinglePermission && hasAnyPermission;
    };
  },
  hasAchievement: (state) => {
    return (achievement) => {
      return (state.achievements || []).includes(achievement);
    };
  },
  achievementsLoaded: (state) => {
    return state.achievementsLoaded;
  },
  allPermissions: (state) => {
    return state.allPermissions || [];
  },
  usersTimezones: (state) => {
    return state.usersTimezones;
  },
};

const formatUsers = (arr) => {
  return arr.map((user) => {
    return {
      label: user.name,
      value: user.id,
      role: user.role,
      photo: user.photo,
    };
  });
};

const getName = (user) => {
  return user?.name;
};

const filterUsers = (arr, search) => {
  const users = arr
    .map((user) => {
      const fullName = getName(user);
      return Object.assign(clone(user), {
        fullName: highlightSearch(fullName, search),
      });
    })
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

  if (!search) return users;

  return users.filter(({ name }) => {
    return compareStrings(name, search);
  });
};
